import React, { useMemo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import ContinueButtonBar from '../components/ContinueButtonBar';
import ChatSaveErrorBanner from '../components/ChatSaveErrorBanner';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { useWheelLocale } from '../hooks/useWheelLocale';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import { interpolateI18n } from '../utils/interpolateI18n';
import HireHealthSurveyChatComposer from './HireHealthSurveyChatComposer';
import HireHealthSurveyChatThread from './HireHealthSurveyChatThread';
import { useHireHealthSurveyFlow } from './useHireHealthSurveyFlow';

export interface HireHealthSurveyChatFlowProps {
  documentTitle: string;
  currentEmail?: string | null;
  onBack: () => void;
  onOpenPdfPreview?: () => void;
}

const HireHealthSurveyChatFlow: React.FC<HireHealthSurveyChatFlowProps> = ({
  documentTitle,
  onBack,
  onOpenPdfPreview,
  currentEmail,
}) => {
  const { t } = useLanguage();
  const wheelLocale = useWheelLocale();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);

  const flow = useHireHealthSurveyFlow(currentEmail, () => {}, t);

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.hireHealthSurvey.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const greeting = `${t('submitDocuments.hireHealthSurvey.greetingPrefix')} ${documentTitle}`;
  const atReview = flow.step === 'finalConfirm' && flow.expandedSectionIndex === null;
  const { showIntro: showPreviewIntroBubble, showCta: showPreviewPdfCta } = useStaggeredPreviewCta(atReview);

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            <AssistantBotRow animateIn>{greeting}</AssistantBotRow>
            {flow.isLoading ? (
              <AssistantBotRow animateIn>{t('submitDocuments.hireHealthSurvey.loading')}</AssistantBotRow>
            ) : (
              <HireHealthSurveyChatThread flow={flow} />
            )}
            {atReview && showPreviewIntroBubble && (
              <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
            )}
            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            <ChatSaveErrorBanner message={flow.saveError} />
            {atReview && showPreviewPdfCta && onOpenPdfPreview && (
              <PdfPreviewActionButton
                label={t('submitDocuments.hireHealthSurvey.previewPdf')}
                onClick={() => {
                  void (async () => {
                    const ok = await flow.handleFinalComplete();
                    if (ok) onOpenPdfPreview();
                  })();
                }}
              />
            )}
            {flow.step === 'finalConfirm' && flow.expandedSectionIndex !== null ? (
              <ContinueButtonBar
                onContinue={flow.collapseEditReview}
                label={t('submitDocuments.hireHealthSurvey.backToReview')}
              />
            ) : !flow.isLoading ? (
              <HireHealthSurveyChatComposer flow={flow} wheelLocale={wheelLocale} />
            ) : null}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default HireHealthSurveyChatFlow;
